import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Users, 
  Sparkles, 
  Compass, 
  Calendar, 
  Award,
  ChevronRight,
  ShieldCheck,
  HeartHandshake
} from 'lucide-react';
import { usePeriod } from './PeriodContext';

function Home() {
  const { selectedPeriod, periodImages } = usePeriod();
  const [scrollY, setScrollY] = useState(0);
  const [tilt, setTilt] = useState({ rotateX: 0, rotateY: 0, moveX: 0, moveY: 0 });

  // Parallax Scroll Listener
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 3D Scene Mouse Movement (Subtle, balanced & elegant)
  const handleMouseMove = (e) => {
    const { clientX, clientY } = e;
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    // Normalize coordinates between -1 and 1
    const x = (clientX - width / 2) / (width / 2);
    const y = (clientY - height / 2) / (height / 2);

    setTilt({
      rotateX: -y * 6,  // kemiringan halus maksimal 6 derajat
      rotateY: x * 7,   // kemiringan halus maksimal 7 derajat
      moveX: x * 10,    // pergeseran halus maksimal 10px
      moveY: y * 10,    // pergeseran halus maksimal 10px
    });
  };

  const handleMouseLeave = () => {
    setTilt({ rotateX: 0, rotateY: 0, moveX: 0, moveY: 0 });
  };

  // Background image berdasarkan periode
  const backgroundImage = periodImages?.backgroundImage 
    ? periodImages.backgroundImage 
    : (selectedPeriod === '2025-2026' ? '/bg.jpg' : '/fkpo_baru.jpeg');

  // Hero image berdasarkan periode
  const heroImage = periodImages?.homeImage 
    ? periodImages.homeImage 
    : (selectedPeriod === '2025-2026' ? '/1.JPEG' : '/fkpo_baru.jpeg');

  const stats = [
    { label: 'Sekolah Terhubung', value: '40+', icon: Compass },
    { label: 'Pengurus & Anggota', value: '60+', icon: Users },
    { label: 'Agenda Kolaboratif', value: '15+', icon: Calendar },
    { label: 'Tahun Kontribusi', value: '8+', icon: Award },
  ];

  const highlights = [
    {
      title: 'Ruang Kolaborasi Pelajar',
      desc: 'Wadah resmi pengurus OSIS se-Kulon Progo untuk menyatukan ide, gagasan kreatif, dan program berdampak.',
      icon: Users,
      badge: 'Sinergi'
    },
    {
      title: 'Kepemimpinan Berkarakter',
      desc: 'Membina calon pemimpin masa depan melalui pelatihan manajemen, etika, dan aksi sosial nyata di masyarakat.',
      icon: ShieldCheck,
      badge: 'Karakter'
    },
    {
      title: 'Aksi Nyata & Kepedulian',
      desc: 'Aktif turun ke masyarakat lewat program sosial seperti Rewang Bersinar dan pengabdian lintas sekolah.',
      icon: HeartHandshake,
      badge: 'Dampak Sosial'
    }
  ];

  return (
    <div 
      onMouseMove={handleMouseMove} 
      onMouseLeave={handleMouseLeave}
      className="relative min-h-screen bg-slate-950 text-slate-100 overflow-hidden"
      style={{ perspective: '1200px' }}
    >
      {/* --- STATIC BACKGROUND LAYER (NO ZOOM) --- */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20 filter blur-[1px] pointer-events-none"
        style={{ 
          backgroundImage: `url(${backgroundImage})`,
          // Geser vertikal sangat subtle tanpa zoom sama sekali
          transform: `translateY(${scrollY * 0.15}px)`,
        }}
      />

      {/* 3D Ambient Grid Floating Plane */}
      <div 
        className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none transition-transform duration-300 ease-out" 
        style={{
          transform: `translate(${tilt.moveX * -0.4}px, ${tilt.moveY * -0.4 + scrollY * 0.08}px) rotateX(${tilt.rotateX * 0.2}deg)`
        }}
      />

      {/* Floating 3D Depth Spheres */}
      <div 
        className="absolute top-20 left-[-60px] w-[450px] h-[450px] bg-indigo-600/20 rounded-full blur-[130px] pointer-events-none transition-transform duration-500 ease-out"
        style={{ transform: `translate(${tilt.moveX * 1.2}px, ${tilt.moveY * 1.2 + scrollY * -0.15}px)` }}
      />
      <div 
        className="absolute top-1/3 right-[-80px] w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[140px] pointer-events-none transition-transform duration-500 ease-out"
        style={{ transform: `translate(${tilt.moveX * -1.2}px, ${tilt.moveY * -1.2 + scrollY * 0.12}px)` }}
      />
      <div 
        className="absolute bottom-10 left-1/3 w-[500px] h-[350px] bg-pink-600/15 rounded-full blur-[140px] pointer-events-none transition-transform duration-500 ease-out"
        style={{ transform: `translate(${tilt.moveX * 0.8}px, ${tilt.moveY * 0.8 + scrollY * -0.08}px)` }}
      />

      {/* Main Container with 3D Depth */}
      <div className="relative z-10 flex flex-col justify-between min-h-screen pt-28 pb-16">
        
        {/* --- HERO SECTION --- */}
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 my-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16 pt-4 pb-16">
            
            {/* Bagian Teks (Kiri) dengan 3D Parallax Offset */}
            <div 
              className="flex-1 text-center lg:text-left space-y-6 max-w-2xl mx-auto lg:mx-0 transition-transform duration-300 ease-out animate-enter-up"
              style={{ 
                transform: `translate3d(${tilt.moveX * -0.8}px, ${tilt.moveY * -0.8 + scrollY * -0.06}px, 20px) rotateY(${tilt.rotateY * 0.4}deg)`,
                transformStyle: 'preserve-3d'
              }}
            >
              
              {/* Badge Announcement dengan Floating & Pulsing Effect */}
              <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-slate-900/80 border border-indigo-500/30 backdrop-blur-xl text-indigo-300 text-xs sm:text-sm font-semibold shadow-xl shadow-indigo-950/40 hover:scale-105 transition-transform cursor-default animate-float-badge">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                </span>
                <span className="tracking-wide">Wadah Aspirasi & Sinergi Pelajar DIY</span>
                <Sparkles className="w-4 h-4 text-indigo-400 ml-0.5 animate-pulse" />
              </div>

              {/* Headline dengan Gradient Glow yang bergerak (animate-gradient-x) */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight leading-[1.12]">
                Satukan Langkah, <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-300 via-pink-400 to-indigo-400 animate-gradient-x drop-shadow-[0_0_35px_rgba(168,85,247,0.35)]">
                  Wujudkan Perubahan.
                </span>
              </h1>

              {/* Subheading */}
              <p className="text-slate-300/90 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
                Forum Komunikasi Pengurus OSIS Kabupaten Kulon Progo hadir sebagai ekosistem kolaborasi strategis dalam mencetak generasi pelajar yang progresif, berintegritas, dan berdaya saing.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3.5 justify-center lg:justify-start pt-2">
                <Link 
                  to="/about" 
                  className="relative group overflow-hidden flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 via-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-xl shadow-indigo-600/30 hover:shadow-indigo-600/50 hover:scale-[1.03] active:scale-95 text-sm sm:text-base animate-pulse-glow"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Mengenal Kami
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                </Link>

                <Link 
                  to="/kegiatan" 
                  className="flex items-center justify-center gap-2 bg-slate-900/80 hover:bg-slate-800 text-slate-200 px-7 py-4 rounded-2xl font-semibold transition-all border border-slate-700/80 hover:border-indigo-500/50 backdrop-blur-md shadow-lg text-sm sm:text-base hover:scale-[1.03] active:scale-95 group"
                >
                  <Calendar className="w-4 h-4 text-indigo-400 group-hover:rotate-12 transition-transform" />
                  <span>Agenda Kegiatan</span>
                </Link>

                <Link 
                  to="/daftar2027" 
                  className="flex items-center justify-center gap-2 bg-slate-900/60 hover:bg-indigo-950/60 text-indigo-300 hover:text-indigo-200 px-6 py-4 rounded-2xl font-semibold transition-all border border-indigo-500/20 hover:border-indigo-500/40 backdrop-blur-md text-sm sm:text-base hover:scale-[1.03]"
                >
                  <span>Open Recruitment</span>
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Quick Period Info */}
              <div className="pt-2 flex items-center justify-center lg:justify-start gap-4 text-xs text-slate-400 font-medium">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  Periode Aktif: <strong className="text-slate-200">{selectedPeriod}</strong>
                </span>
                <span>•</span>
                <span>Naungan Resmi BPO & Balai Dikmen</span>
              </div>
            </div>

            {/* Bagian Gambar (Kanan) dengan 3D Space Perspective Tilt & Depth */}
            <div 
              className="flex-1 relative w-full max-w-lg lg:max-w-none flex justify-center transition-transform duration-300 ease-out"
              style={{ 
                transform: `perspective(1000px) rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg) translate3d(${tilt.moveX * 1.1}px, ${tilt.moveY * 1.1 + scrollY * -0.05}px, 40px)`,
                transformStyle: 'preserve-3d'
              }}
            >
              
              {/* Outer Glow Halo 3D Depth */}
              <div 
                className="absolute inset-0 bg-gradient-to-tr from-indigo-500/35 via-purple-500/25 to-pink-500/20 blur-3xl rounded-3xl -z-10 animate-pulse-slow"
                style={{ transform: 'translateZ(-50px)' }}
              />

              {/* Interactive 3D Frame */}
              <div 
                className="relative group p-4 sm:p-5 bg-slate-900/80 border border-slate-700/60 rounded-3xl backdrop-blur-2xl shadow-2xl shadow-indigo-950/60 transition-all duration-300 hover:border-indigo-400/60"
                style={{ transformStyle: 'preserve-3d' }}
              >
                
                <div 
                  className="relative overflow-hidden rounded-2xl aspect-[4/3] sm:aspect-[16/11] shadow-2xl"
                  style={{ transform: 'translateZ(25px)' }}
                >
                  <img
                    src={heroImage}
                    alt="FKPO Kulon Progo"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/bg.jpg';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/20 to-transparent"></div>
                  
                  {/* Floating Glass Pill on Image yang mengapung di depan layer 3D */}
                  <div 
                    className="absolute bottom-4 left-4 right-4 bg-slate-900/90 backdrop-blur-xl border border-white/15 p-4 rounded-2xl flex items-center justify-between shadow-2xl transition-transform duration-300"
                    style={{ transform: 'translateZ(45px)' }}
                  >
                    <div>
                      <p className="text-[11px] uppercase font-bold text-indigo-400 tracking-wider">FKPO Kulon Progo</p>
                      <p className="text-sm font-semibold text-white">Sinergi Pemimpin Pelajar Kulon Progo</p>
                    </div>
                    <span className="px-3.5 py-1.5 bg-indigo-600/40 border border-indigo-400/50 rounded-xl text-indigo-300 text-xs font-bold shadow-lg">
                      {selectedPeriod}
                    </span>
                  </div>
                </div>

              </div>

            </div>

          </div>

          {/* --- STATS COUNTER STRIP with 3D Depth & Floating Motion --- */}
          <div 
            className="mt-6 mb-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto transition-transform duration-300 ease-out"
            style={{ 
              transform: `translate3d(${tilt.moveX * 0.4}px, ${tilt.moveY * 0.4 + scrollY * -0.04}px, 15px) rotateX(${tilt.rotateX * 0.3}deg)`,
              transformStyle: 'preserve-3d'
            }}
          >
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div 
                  key={idx}
                  className="bg-slate-900/60 border border-slate-800/80 hover:border-indigo-500/50 backdrop-blur-xl rounded-2xl p-5 text-center transition-all duration-300 hover:-translate-y-3 hover:shadow-2xl hover:shadow-indigo-950/60 group cursor-default animate-float-badge"
                  style={{ animationDelay: `${idx * 0.3}s`, transformStyle: 'preserve-3d' }}
                >
                  <div className="w-11 h-11 rounded-xl bg-indigo-600/10 border border-indigo-500/20 text-indigo-400 mx-auto mb-3 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white group-hover:scale-110 transition-all shadow-md">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="text-2xl sm:text-3xl font-black text-white tracking-tight group-hover:text-indigo-300 transition-colors">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-slate-400 font-medium mt-1">{stat.label}</div>
                </div>
              );
            })}
          </div>

          {/* --- 3 PILLARS / HIGHLIGHTS SECTION with 3D Movement --- */}
          <div 
            className="mt-8 mb-12 max-w-6xl mx-auto transition-transform duration-300 ease-out"
            style={{ 
              transform: `translate3d(${tilt.moveX * -0.3}px, ${tilt.moveY * -0.3 + scrollY * -0.02}px, 10px)`,
              transformStyle: 'preserve-3d'
            }}
          >
            <div className="text-center mb-10 space-y-3">
              <span className="text-xs uppercase tracking-widest font-extrabold text-indigo-400 px-3 py-1 bg-indigo-500/10 rounded-full border border-indigo-500/20 animate-float-badge inline-block">
                Pilar Pergerakan
              </span>
              <h2 className="text-2xl sm:text-4xl font-black text-white">
                Fokus Utama Organisasi
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {highlights.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div 
                    key={idx}
                    className="group relative bg-slate-900/50 hover:bg-slate-900/90 border border-slate-800/80 hover:border-indigo-500/50 backdrop-blur-xl rounded-3xl p-7 transition-all duration-400 hover:-translate-y-3 shadow-xl hover:shadow-2xl hover:shadow-indigo-950/60 flex flex-col justify-between overflow-hidden animate-float-card"
                    style={{ animationDelay: `${idx * 0.5}s`, transformStyle: 'preserve-3d' }}
                  >
                    {/* Corner Ambient glow */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl group-hover:bg-indigo-500/25 transition-colors pointer-events-none animate-pulse-slow" />

                    <div>
                      <div className="flex items-center justify-between mb-5">
                        <div className="w-12 h-12 rounded-2xl bg-indigo-600/10 border border-indigo-500/20 text-indigo-400 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white group-hover:scale-110 transition-all shadow-md">
                          <Icon className="w-6 h-6" />
                        </div>
                        <span className="text-xs font-semibold px-3 py-1 rounded-full bg-slate-800/90 border border-slate-700 text-slate-300">
                          {item.badge}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2.5 group-hover:text-indigo-300 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-slate-400 text-sm leading-relaxed">
                        {item.desc}
                      </p>
                    </div>

                    <div className="pt-6 mt-4 border-t border-slate-800/60 flex items-center gap-1.5 text-xs font-semibold text-indigo-400 group-hover:text-indigo-300">
                      <span>Pelajari aksi kami</span>
                      <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1.5 transition-transform" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </main>
      </div>
    </div>
  );
}

export default Home;

